import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import '@fontsource-variable/hanken-grotesk';

import './tokens.css';

import { TenantThemeProvider } from '@/shared/branding/TenantThemeProvider';

import { getStyleNonce } from './cspNonce';
import { TenantDatesProvider } from './dates';
import { createQueryClient } from './query-client';
import { createAppRouter } from './router';
import { theme } from './theme';

import '@/i18n';

const container = document.getElementById('root');

if (!container) {
  throw new Error('The #root element is missing from index.html.');
}

const queryClient = createQueryClient();
const router = createAppRouter(queryClient);

createRoot(container).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light" getStyleNonce={getStyleNonce}>
      <TenantDatesProvider>
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <TenantThemeProvider>
            <RouterProvider router={router} />
          </TenantThemeProvider>
        </QueryClientProvider>
      </TenantDatesProvider>
    </MantineProvider>
  </StrictMode>,
);

// PWA mínima (`AM-132 point 13`, D17): el service worker solo se registra en producción - un
// `pnpm dev` nunca sirve un archivo con el `BUILD_ID` real, y no tiene sentido cachear ese shell.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Sin service worker el sitio sigue funcionando igual (D17): no hay nada que reportar aquí.
    });
  });
}
